import { type ActorView, getProfile } from "../../queries.ts";
import { formatError, publicTransport, RpcClient } from "../api.ts";
import {
  accountsSignal,
  type AuthenticatedState,
  authState,
  type LoginAccount,
  refreshAccounts,
  resolveLoginAccount,
  signIn,
  signOut,
  switchAccount,
} from "../auth.ts";
import { config } from "../config.ts";
import { Avatar } from "./actor.tsx";
import { dropdown } from "./menu.tsx";

export function AuthPanel(): Element {
  const target = <div class="auth-panel"></div> as HTMLElement;
  const trigger = <button class="auth-trigger" type="button"></button> as HTMLButtonElement;
  const menu = <div class="menu account-menu" role="menu"></div> as HTMLElement;
  const list = <div class="account-list"></div>;
  const addToggle = (
    <button class="account-add-toggle" type="button" role="menuitem">Add account</button>
  ) as HTMLButtonElement;
  const addWrap = <div class="account-add" hidden></div> as HTMLElement;
  const status = <small class="account-status" aria-live="polite"></small>;
  const warning = <small class="account-warning"></small> as HTMLElement;
  addWrap.append(SignInForm());
  menu.append(list, addToggle, addWrap, status, warning);
  const wrapper = <div class="auth-account"></div>;
  wrapper.append(trigger, menu);
  target.append(wrapper);

  // The shared dropdown dismisses on any click, but the inline sign-in form
  // needs clicks to reach its own inputs.
  menu.addEventListener("click", (event) => event.stopPropagation());
  dropdown(trigger, menu, new AbortController().signal);

  const identities = new Map<string, ActorView>();
  let identityFlight = 0;
  let busy: string | undefined;

  const refreshIdentities = (active: string, accounts: readonly string[]): Promise<void> => {
    const flight = ++identityFlight;
    const missing = [...new Set([active, ...accounts])].filter((did) => !identities.has(did));
    if (!missing.length) return Promise.resolve();
    const client = new RpcClient(publicTransport(config.appView));
    return Promise.all(
      missing.map((did) =>
        client.call(getProfile, { parameters: { actor: did } }).then((value) => {
          if (flight !== identityFlight) return false;
          identities.set(did, value.actor);
          return true;
        }).catch(() => {
          if (flight !== identityFlight) return false;
          identities.set(did, { did: did as `did:${string}` });
          return true;
        })
      ),
    ).then((done) => {
      if (flight === identityFlight && done.some(Boolean)) render();
    });
  };

  const paintTrigger = (
    state: AuthenticatedState,
    identity: ActorView | undefined,
  ): void => {
    const label = labelFor(state.did, identity);
    trigger.replaceChildren(
      avatarFor(state.did, identity),
      <span class="account-identity">
        <strong>{label}</strong>
        <span>{subFor(state.did, identity)}</span>
      </span>,
      <span class="auth-chevron" aria-hidden="true"></span>,
    );
    trigger.title = state.did;
    trigger.setAttribute("aria-label", `Accounts. Signed in as ${label}.`);
  };

  const paintList = (
    state: AuthenticatedState,
    accounts: readonly string[],
  ): void => {
    list.replaceChildren();
    const ordered = [state.did, ...accounts.filter((did) => did !== state.did)];
    for (const did of ordered) {
      const identity = identities.get(did);
      const current = did === state.did;
      const row = <div class="account-row"></div>;
      const pick = (
        <button
          class="account-switch"
          type="button"
          role="menuitemradio"
          aria-checked={current ? "true" : "false"}
          title={current ? "Current account" : `Switch to ${labelFor(did, identity)}`}
        >
        </button>
      ) as HTMLButtonElement;
      pick.disabled = current || busy !== undefined;
      pick.append(
        avatarFor(did, identity),
        <span class="account-identity">
          <strong>{labelFor(did, identity)}</strong>
          <span>{subFor(did, identity)}</span>
        </span>,
        <span class="account-check" aria-hidden="true">{current ? "✓" : ""}</span>,
      );
      if (!current) {
        pick.addEventListener("click", () => {
          busy = did;
          status.textContent = `Switching to ${labelFor(did, identity)}…`;
          render();
          void switchAccount(did).then(() => {
            busy = undefined;
            status.textContent = "";
            render();
            trigger.click();
          }).catch((error) => {
            busy = undefined;
            if (error instanceof DOMException && error.name === "AbortError") {
              render();
              return;
            }
            status.textContent = formatError(error);
            render();
          });
        });
      }
      const remove = (
        <button
          class="account-signout"
          type="button"
          role="menuitem"
          aria-label={`Sign out ${labelFor(did, identity)}`}
          title={`Sign out ${labelFor(did, identity)}`}
        >
          ×
        </button>
      ) as HTMLButtonElement;
      remove.disabled = busy !== undefined;
      remove.addEventListener("click", () => {
        busy = did;
        status.textContent = "Signing out…";
        render();
        void signOut(did).then(() => {
          busy = undefined;
          status.textContent = "";
          render();
        }).catch((error) => {
          busy = undefined;
          status.textContent = formatError(error);
          render();
        });
      });
      row.append(pick, remove);
      list.append(row);
    }
  };

  const render = (): void => {
    const state = authState.get();
    const accounts = accountsSignal.get();
    target.hidden = state.status !== "authenticated";
    if (state.status !== "authenticated") return;
    paintTrigger(state, identities.get(state.did));
    paintList(state, accounts);
    warning.textContent = state.persistenceWarning ?? "";
    warning.hidden = !state.persistenceWarning;
    void refreshIdentities(state.did, accounts);
  };

  trigger.addEventListener("click", () => {
    if (menu.hidden) return;
    refreshAccounts();
    const state = authState.get();
    if (state.status === "authenticated") void refreshIdentities(state.did, accountsSignal.get());
  });
  addToggle.addEventListener("click", () => {
    addWrap.hidden = !addWrap.hidden;
    addToggle.textContent = addWrap.hidden ? "Add account" : "Hide sign-in";
    if (!addWrap.hidden) (addWrap.querySelector("input") as HTMLInputElement | null)?.focus();
  });
  authState.subscribeImmediate(render);
  accountsSignal.subscribeImmediate(render);
  return target;
}

function labelFor(did: string, identity: ActorView | undefined): string {
  if (identity?.displayName) return identity.displayName;
  if (identity?.handle) return `@${identity.handle}`;
  return shortDid(did);
}

function subFor(did: string, identity: ActorView | undefined): string {
  if (identity?.displayName) return identity.handle ? `@${identity.handle}` : shortDid(did);
  if (identity?.handle) return shortDid(did);
  return did;
}

function avatarFor(did: string, identity: ActorView | undefined): Element {
  return Avatar(identity ?? { did: did as `did:${string}` }, "avatar account-avatar");
}

/** Re-authorization prompt for sessions missing a grant; sign-in round-trips through OAuth. */
export function AuthorizePrompt(
  did: string,
  message: string,
  label: string,
  returnTo: string,
  signal: AbortSignal,
): Element[] {
  const status = <p class="status">{message}</p>;
  const button = <button type="button">{label}</button> as HTMLButtonElement;
  button.addEventListener("click", () => {
    button.disabled = true;
    void signIn(did, returnTo).catch((error) => {
      if (signal.aborted) return;
      status.textContent = formatError(error);
      button.disabled = false;
    });
  });
  return [status, button];
}

export function SignInForm(message?: string, signal?: AbortSignal): Element {
  const input = (
    <input
      aria-label="Account handle or DID"
      autocomplete="username"
      placeholder="handle or did:…"
      required
    />
  ) as HTMLInputElement;
  const submit = <button type="submit">Sign in</button> as HTMLButtonElement;
  const status = <small class="auth-message" aria-live="polite">{message || ""}</small>;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let request: AbortController | undefined;
  let resolved: (LoginAccount & { readonly identifier: string }) | undefined;

  const lookup = async (
    identifier: string,
    controller: AbortController,
  ): Promise<LoginAccount | undefined> => {
    status.textContent = "Finding your PDS…";
    try {
      const account = await resolveLoginAccount(identifier, controller.signal);
      if (request !== controller) return;
      resolved = { ...account, identifier };
      status.textContent = `Sign in via: ${new URL(account.pds).host}`;
      return account;
    } catch (error) {
      if (request !== controller) return;
      resolved = undefined;
      status.textContent = formatError(error);
    }
  };

  input.addEventListener("input", () => {
    clearTimeout(timer);
    request?.abort();
    request = undefined;
    resolved = undefined;
    const identifier = normalizeIdentifier(input.value);
    status.textContent = "";
    if (!identifier.includes(".") && !identifier.startsWith("did:")) return;
    timer = setTimeout(() => {
      const controller = new AbortController();
      request = controller;
      void lookup(identifier, controller);
    }, 500);
  });

  signal?.addEventListener("abort", () => {
    clearTimeout(timer);
    request?.abort();
  }, { once: true });

  return (
    <form
      class="sign-in"
      _onsubmit={(event) => {
        event.preventDefault();
        clearTimeout(timer);
        request?.abort();
        const identifier = normalizeIdentifier(input.value);
        submit.disabled = true;
        void (async () => {
          const cached = resolved?.identifier === identifier ? resolved : undefined;
          let account: LoginAccount | undefined = cached;
          if (!account) {
            const controller = new AbortController();
            request = controller;
            account = await lookup(identifier, controller);
          }
          if (!account) {
            submit.disabled = false;
            return;
          }
          status.textContent = `Opening ${new URL(account.pds).host}…`;
          try {
            await signIn(account.did, location.pathname + location.search);
          } catch (error) {
            status.textContent = formatError(error);
            submit.disabled = false;
          }
        })();
      }}
    >
      <div>
        <span class="sr-only">Account handle or DID</span>
        {input}
        {submit}
      </div>
      {status}
    </form>
  );
}

function normalizeIdentifier(identifier: string): string {
  const normalized = identifier.trim().replace(/^@/, "");
  return normalized.startsWith("did:") ? normalized : normalized.toLowerCase();
}

function shortDid(did: string): string {
  return did.length > 27 ? `${did.slice(0, 15)}…${did.slice(-7)}` : did;
}
